import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Bell, CalendarBlank, Note as NoteIcon, Receipt, CheckSquare } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Slide-in notifications panel.
 *
 * Aggregates client-side signals that don't need dedicated backend endpoints yet:
 *  - Reservations arriving today / tomorrow (check-in reminders)
 *  - Open reservation tasks
 *  - Recent internal notes (past 14 days)
 *  - Draft invoices waiting to be issued
 *
 * "Seen" timestamps are persisted in localStorage per-tenant.
 */
export default function NotificationsPanel({ open, onClose, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rw-notif-filter") || "[]"); }
    catch { return []; }
  });
  const toggleFilter = (kind) => setFilter((prev) => {
    const next = prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind];
    try { localStorage.setItem("rw-notif-filter", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const today = new Date();
        const in3d = new Date(today.getTime() + 3 * 86400000);
        const [{ data: reservations }, { data: invoices }] = await Promise.all([
          api.get("/reservations?limit=200"),
          api.get("/invoices?status=draft&limit=200"),
        ]);
        const evts = [];

        const iso = (d) => d.toISOString().slice(0, 10);
        const todayIso = iso(today);
        const in3dIso = iso(in3d);

        (reservations || []).forEach((r) => {
          if (r.check_in >= todayIso && r.check_in <= in3dIso && r.status !== "cancelled" && r.status !== "checked_in" && r.status !== "checked_out") {
            evts.push({
              id: `arr-${r.id}`,
              kind: "arrival",
              title: `Dolazak ${r.primary_guest_name}`,
              subtitle: `${r.check_in} • ${r.code}`,
              ts: r.check_in,
              raw: r,
            });
          }
          (r.tasks || []).filter((t) => !t.done).forEach((t) => {
            evts.push({
              id: `task-${r.id}-${t.id}`,
              kind: "task",
              title: t.title,
              subtitle: `${r.primary_guest_name} • ${r.code}${t.due_date ? ` • do ${t.due_date}` : ""}`,
              ts: t.due_date || r.check_in,
              raw: r,
            });
          });
          if ((r.notes || "").trim()) {
            evts.push({
              id: `note-${r.id}`,
              kind: "note",
              title: (r.notes || "").split("\n")[0].slice(0, 80),
              subtitle: `Napomena • ${r.primary_guest_name}`,
              ts: r.check_in,
              raw: r,
            });
          }
        });

        (invoices || []).forEach((inv) => {
          evts.push({
            id: `invd-${inv.id}`,
            kind: "invoice",
            title: `Račun u pripremi za ${inv.client_name || "…"}`,
            subtitle: `${Number(inv.total || 0).toFixed(2)} ${inv.currency} • Zaključi ili obriši`,
            ts: inv.issue_date,
            raw: inv,
          });
        });

        // Sort desc by ts (most-imminent first)
        evts.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
        setItems(evts);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  // Mark "seen" when opened
  useEffect(() => {
    if (open) localStorage.setItem("rw-notifications-seen", String(Date.now()));
  }, [open]);

  if (!open) return null;

  const Wrap = ({ children }) => (embedded ? <div data-testid="reminders-list">{children}</div> : createPortal(
    <div className="fixed inset-0 z-[70]" data-testid="notifications-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-[420px] overflow-hidden bg-card shadow-2xl">{children}</aside>
    </div>, document.body));

  return (
    <Wrap>
        {!embedded && <div className="flex items-center justify-between border-b border-border bg-muted/40 px-5 py-4">
          <div className="flex items-center gap-2">
            <Bell size={18} weight="fill" className="text-primary" />
            <h2 className="text-base font-semibold">Obavijesti</h2>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
              {items.length}
            </span>
          </div>
          <button onClick={onClose} className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="notifications-close">
            <X size={16} weight="bold" />
          </button>
        </div>}

        {/* Kind filters (empty selection = show all) */}
        <div className="flex flex-wrap gap-2 border-b border-border bg-muted/20 px-5 py-2" data-testid="notifications-filters">
          {[
            { key: "arrival", label: "Dolasci" },
            { key: "task", label: "Zadaci" },
            { key: "note", label: "Napomene" },
            { key: "invoice", label: "Računi" },
          ].map((k) => {
            const active = filter.length === 0 || filter.includes(k.key);
            return (
              <button
                key={k.key}
                onClick={() => toggleFilter(k.key)}
                data-testid={`notif-filter-${k.key}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
                }`}
              >
                {k.label}
              </button>
            );
          })}
          {filter.length > 0 && (
            <button onClick={() => { setFilter([]); try { localStorage.setItem("rw-notif-filter", "[]"); } catch { /* ignore */ } }} className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground" data-testid="notif-filter-reset">
              Prikaži sve
            </button>
          )}
        </div>

        <div className={embedded ? "" : "h-full overflow-y-auto pb-24"}>
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Učitavanje...</div>
          ) : (() => {
            const visible = filter.length === 0 ? items : items.filter((n) => filter.includes(n.kind));
            if (visible.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                  <Bell size={44} weight="thin" className="text-muted-foreground/40" />
                  <p className="mt-3 text-sm text-muted-foreground">
                    {items.length === 0 ? "Nema novih obavijesti" : "Nema obavijesti za odabrane filtere"}
                  </p>
                </div>
              );
            }
            return (
              <ul className="divide-y divide-border">
                {visible.map((n) => (
                  <NotificationRow key={n.id} n={n} onClose={onClose} />
                ))}
              </ul>
            );
          })()}
        </div>
    </Wrap>
  );
}

function NotificationRow({ n }) {
  const meta = KIND_META[n.kind] || KIND_META.note;
  const Icon = meta.icon;
  return (
    <li className="flex items-start gap-3 px-5 py-3 hover:bg-muted/30" data-testid={`notif-${n.kind}-${n.id}`}>
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.color}`}>
        <Icon size={14} weight="bold" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{n.title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{n.subtitle}</p>
      </div>
    </li>
  );
}

const KIND_META = {
  arrival: { icon: CalendarBlank, color: "bg-sky-100 text-sky-700" },
  task: { icon: CheckSquare, color: "bg-amber-100 text-amber-700" },
  note: { icon: NoteIcon, color: "bg-violet-100 text-violet-700" },
  invoice: { icon: Receipt, color: "bg-emerald-100 text-emerald-700" },
};

// ---------------------------------------------------------------------------
// Small hook — count of new-since-last-seen. Reused by the bell button.
// ---------------------------------------------------------------------------
export function useUnseenCount() {
  const [count, setCount] = useState(0);

  const compute = async () => {
    try {
      const seenTs = Number(localStorage.getItem("rw-notifications-seen") || 0);
      const [{ data: reservations }, { data: invoices }] = await Promise.all([
        api.get("/reservations?limit=200"),
        api.get("/invoices?status=draft&limit=200"),
      ]);
      const today = new Date().toISOString().slice(0, 10);
      let c = 0;
      (reservations || []).forEach((r) => {
        // Any check-in in next 3 days that we haven't opened the panel since being modified
        if (r.check_in >= today && new Date(r.updated_at || 0).getTime() > seenTs) c += 1;
        (r.tasks || []).forEach((t) => {
          if (!t.done && new Date(t.created_at || 0).getTime() > seenTs) c += 1;
        });
      });
      (invoices || []).forEach((inv) => {
        if (new Date(inv.updated_at || 0).getTime() > seenTs) c += 1;
      });
      setCount(c);
    } catch { /* offline is fine */ }
  };

  useEffect(() => {
    compute();
    const t = setInterval(compute, 60000); // 1 min poll
    return () => clearInterval(t);
  }, []);

  return { count, refresh: compute };
}
